/**
 * Transactions API Service
 * Handles all transaction-related API calls
 * Based on SPECIFICATION.md Section 7.7
 */
import * as Sentry from '@sentry/react';
import api from './api';
import { similarTransactionsService } from './similarTransactions';
import type { Transaction, TransactionFilters, PaginatedResponse } from '@/types';

// ============================================================================
// Types
// ============================================================================

export type TransactionType = 'income' | 'expense' | 'transfer';
export type TransactionStatus = 'pending' | 'posted' | 'canceled';

export interface TransactionSplit {
  id: string;
  transactionId: string;
  categoryId: string;
  categoryName?: string;
  amount: number;
  notes?: string;
}

/**
 * Summary of a paired transaction for transfer display.
 * WP-TRANSFER-4: Used to show details of the paired transaction in TransferPairInfo.
 */
export interface TransferPairTransaction {
  id: string;
  name: string;
  amount: number;
  date: string;
  accountId: string;
  accountName: string;
}

export interface TransactionWithSplits extends Transaction {
  splits?: TransactionSplit[];
  isSplit: boolean;
  isTransfer: boolean;
  categoryName?: string;
  categoryPath?: string;
  /** WP-TRANSFER-4: Target account ID for transfers */
  transferTargetAccountId?: string;
  /** WP-TRANSFER-4: Target account name for display */
  transferTargetAccountName?: string;
  /** WP-TRANSFER-4: Summary of the paired transaction if one exists */
  transferPairTransaction?: TransferPairTransaction;
  /** Transfer pair ID (existing field) */
  transferPairId?: string;
}

export interface TransactionSortOptions {
  field: 'date' | 'amount' | 'name' | 'category' | 'createdAt';
  direction: 'asc' | 'desc';
}

export interface TransactionsListParams extends TransactionFilters {
  page?: number;
  pageSize?: number;
  accountIds?: string[];
  categoryIds?: string[];
  status?: TransactionStatus;
  pending?: boolean;
  hasNotes?: boolean;
  tags?: string[];
  sort?: TransactionSortOptions;
  /**
   * Filter by amount sign: 'positive' (income) or 'negative' (expense).
   * Used for flow drill-down to show only transactions matching the node type.
   */
  amountSign?: 'positive' | 'negative';
}

/**
 * The envelope GET /transactions returns.
 *
 * Rows stay `unknown` - transformTransaction owns their shape, and this
 * endpoint has historically returned both camelCase and snake_case
 * (.claude/rules/ops/type-safety.md), so claiming a row type here would be a
 * fiction. Every field is optional because the reader below already defaults
 * each one.
 */
interface TransactionListEnvelope {
  transactions?: unknown[];
  total?: number;
  limit?: number;
  offset?: number;
}

export interface TransactionUpdatePayload {
  categoryId?: string;
  notes?: string;
  tags?: string[];
  type?: TransactionType;
}

export interface SpendingByCategory {
  categoryId: string;
  categoryName: string;
  categoryPath: string;
  categoryIcon?: string;
  categoryColor?: string;
  total: number;
  transactionCount: number;
  percentOfTotal: number;
  children?: SpendingByCategory[];
}

export interface SpendingByCategoryParams {
  startDate: string;
  endDate: string;
  accountId?: string;
  depth?: number;
}

export interface SpendingSummary {
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  periodStart: string;
  periodEnd: string;
  byCategory: SpendingByCategory[];
}

export interface PeriodSummary {
  totalIncome: number;
  totalExpenses: number;
  net: number;
  incomeCount: number;
  expenseCount: number;
  startDate: string;
  endDate: string;
}

export interface PeriodSummaryParams {
  startDate: string;
  endDate: string;
  excludeTransfers?: boolean;
}

export interface BulkCategorizeRequest {
  transactionIds: string[];
  categoryId: string;
}

export interface BulkCategorizeResponse {
  updatedCount: number;
}

export interface TransactionSearchParams {
  query: string;
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}

export interface TransactionsListResponse {
  transactions: TransactionWithSplits[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface TransactionDetailResponse {
  transaction: TransactionWithSplits;
  account: {
    id: string;
    name: string;
    type: string;
    mask?: string;
  };
  category?: {
    id: string;
    name: string;
    path: string;
    icon?: string;
    color?: string;
  };
}

// ============================================================================
// Transactions Service
// ============================================================================

export const transactionsService = {
  /**
   * Get paginated list of transactions with filters
   */
  getAll: async (params: TransactionsListParams = {}): Promise<TransactionsListResponse> => {
    const queryParams: Record<string, unknown> = {};

    // Backend uses limit/offset, not page/pageSize
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 50;
    queryParams.limit = pageSize;
    queryParams.offset = (page - 1) * pageSize;

    if (params.accountId) queryParams.accountId = params.accountId;
    if (params.accountIds?.length) queryParams.accountIds = params.accountIds.join(',');
    if (params.categoryId) queryParams.categoryId = params.categoryId;
    if (params.categoryIds?.length) queryParams.categoryIds = params.categoryIds.join(',');
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;
    if (params.minAmount !== undefined) queryParams.minAmount = params.minAmount;
    if (params.maxAmount !== undefined) queryParams.maxAmount = params.maxAmount;
    if (params.search) queryParams.search = params.search;
    // WP-BUG-42: Translate frontend 'type' to backend-compatible parameters
    // Backend doesn't have a 'type' param - it uses 'is_transfer' and 'amount_sign'
    if (params.type) {
      switch (params.type) {
        case 'transfer':
          // Backend: is_transfer (boolean) - converted to is_transfer by API client
          queryParams.isTransfer = true;
          break;
        case 'income':
          // Backend: amount_sign='positive' - converted to amount_sign by API client
          queryParams.amountSign = 'positive';
          break;
        case 'expense':
          // Backend: amount_sign='negative' - converted to amount_sign by API client
          queryParams.amountSign = 'negative';
          break;
      }
    }
    if (params.status) queryParams.status = params.status;
    if (params.pending !== undefined) queryParams.pending = params.pending;
    if (params.hasNotes !== undefined) queryParams.hasNotes = params.hasNotes;
    if (params.tags?.length) queryParams.tags = params.tags.join(',');
    if (params.sort) {
      queryParams.sortField = params.sort.field;
      queryParams.sortDirection = params.sort.direction;
    }
    if (params.amountSign) queryParams.amountSign = params.amountSign;

    // Backend returns { transactions, total, limit, offset }
    // Named rather than `any`. Every field is optional and the rows stay
    // `unknown` until transformTransaction maps them, so this describes the
    // envelope without claiming to know the row shape - which is the one thing
    // `any` here was actually buying.
    let response: TransactionListEnvelope;
    try {
      response = await api.get<TransactionListEnvelope>(
        '/transactions',
        queryParams
      );
    } catch (error) {
      // Log API errors for debugging (no PII - just error details)
      if (import.meta.env.DEV) {
        console.error(
          '[transactionsService.getAll] API error:',
          error instanceof Error ? error.message : 'Unknown error'
        );
      }
      // Send to Sentry in production (no PII)
      Sentry.captureException(error, {
        extra: { operation: 'getAll' },
      });
      throw error;
    }

    // Debug logging for development (no PII - just counts and structure)
    if (import.meta.env.DEV) {
      console.debug('[transactionsService.getAll] Response structure:', {
        hasTransactions: 'transactions' in response,
        transactionsLength: response?.transactions?.length ?? 0,
        total: response?.total,
        limit: response?.limit,
        offset: response?.offset,
      });
    }

    // Transform to frontend format with pagination object
    const total = response.total ?? 0;
    const limit = response.limit ?? pageSize;
    const offset = response.offset ?? 0;
    const totalPages = Math.ceil(total / limit);
    const currentPage = Math.floor(offset / limit) + 1;

    // WP-FE-1: Use shared transformTransaction() for consistent field mapping
    // This ensures all fields including transfer-related ones are properly transformed
    const transactions = (response.transactions ?? []).map(transformTransaction);

    return {
      transactions,
      pagination: {
        page: currentPage,
        pageSize: limit,
        totalItems: total,
        totalPages,
        hasMore: currentPage < totalPages,
      },
    };
  },

  /**
   * Get a single transaction by ID
   */
  getById: async (id: string): Promise<TransactionDetailResponse> => {
    return api.get<TransactionDetailResponse>(`/transactions/${id}`);
  },

  /**
   * Update a transaction (category, notes, tags, type)
   */
  update: async (id: string, payload: TransactionUpdatePayload): Promise<Transaction> => {
    const requestPayload: Record<string, unknown> = {};
    if (payload.categoryId !== undefined) {
      requestPayload.categoryId = payload.categoryId;
    }
    if (payload.notes !== undefined) {
      requestPayload.userNotes = payload.notes;
    }
    if (payload.tags !== undefined) {
      requestPayload.tags = payload.tags;
    }
    if (payload.type !== undefined) {
      requestPayload.type = payload.type;
    }
    return api.patch<Transaction>(`/transactions/${id}`, requestPayload);
  },

  /**
   * Bulk categorize multiple transactions
   * Uses PATCH /v1/transactions/bulk, the one bulk-update endpoint
   * (api-surface.md section 9). POST /transactions/bulk-categorize, called here
   * until WP-BUG-65, never existed in the backend.
   */
  bulkCategorize: async (data: BulkCategorizeRequest): Promise<BulkCategorizeResponse> => {
    const result = await similarTransactionsService.bulkUpdateWithRule({
      transactionIds: data.transactionIds,
      categoryId: data.categoryId,
    });
    return { updatedCount: result.updatedCount };
  },

  /**
   * Search transactions by text query
   */
  search: async (params: TransactionSearchParams): Promise<PaginatedResponse<Transaction>> => {
    const queryParams: Record<string, unknown> = {
      query: params.query,
    };

    if (params.page !== undefined) queryParams.page = params.page;
    if (params.pageSize !== undefined) queryParams.pageSize = params.pageSize;
    if (params.startDate) queryParams.startDate = params.startDate;
    if (params.endDate) queryParams.endDate = params.endDate;

    return api.get<PaginatedResponse<Transaction>>('/transactions/search', queryParams);
  },

  /**
   * Get spending aggregated by category
   */
  getSpendingByCategory: async (params: SpendingByCategoryParams): Promise<SpendingByCategory[]> => {
    const queryParams: Record<string, unknown> = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.accountId) queryParams.accountId = params.accountId;
    if (params.depth !== undefined) queryParams.depth = params.depth;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/transactions/spending-by-category', queryParams);

    const categories = response.categories ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return categories.map((cat: any) => ({
      categoryId: cat.categoryId ?? cat.category_id ?? '',
      categoryName: cat.categoryName ?? cat.category_name ?? 'Unknown',
      categoryPath: cat.categoryPath ?? cat.category_path ?? '',
      categoryIcon: cat.categoryIcon ?? cat.category_icon,
      categoryColor: cat.categoryColor ?? cat.category_color,
      // Convert Decimal strings from backend to numbers
      total: Number(cat.total ?? 0),
      transactionCount: Number(cat.count ?? cat.transactionCount ?? 0),
      percentOfTotal: Number(cat.percentage ?? cat.percentOfTotal ?? 0),
      children: cat.children,
    }));
  },

  /**
   * Get spending summary for a period
   */
  getSpendingSummary: async (
    startDate: string,
    endDate: string,
    accountId?: string
  ): Promise<SpendingSummary> => {
    const queryParams: Record<string, unknown> = {
      startDate,
      endDate,
    };

    if (accountId) queryParams.accountId = accountId;

    return api.get<SpendingSummary>('/transactions/spending-summary', queryParams);
  },

  /**
   * Get period summary (income and expense totals)
   */
  getPeriodSummary: async (params: PeriodSummaryParams): Promise<PeriodSummary> => {
    const queryParams: Record<string, unknown> = {
      startDate: params.startDate,
      endDate: params.endDate,
    };

    if (params.excludeTransfers !== undefined) {
      queryParams.excludeTransfers = params.excludeTransfers;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/transactions/period-summary', queryParams);

    return {
      totalIncome: Number(response.totalIncome ?? response.total_income ?? 0),
      totalExpenses: Number(response.totalExpenses ?? response.total_expenses ?? 0),
      net: Number(response.net ?? 0),
      incomeCount: Number(response.incomeCount ?? response.income_count ?? 0),
      expenseCount: Number(response.expenseCount ?? response.expense_count ?? 0),
      startDate: response.startDate ?? response.start_date ?? params.startDate,
      endDate: response.endDate ?? response.end_date ?? params.endDate,
    };
  },

  // ============================================================================
  // Transaction Splits
  // ============================================================================

  /**
   * Get splits for a transaction
   */
  getSplits: async (transactionId: string): Promise<TransactionSplit[]> => {
    return api.get<TransactionSplit[]>(`/transactions/${transactionId}/splits`);
  },

  /**
   * Create or update splits for a transaction
   */
  setSplits: async (
    transactionId: string,
    splits: Omit<TransactionSplit, 'id' | 'transactionId'>[]
  ): Promise<TransactionSplit[]> => {
    return api.put<TransactionSplit[]>(`/transactions/${transactionId}/splits`, { splits });
  },

  /**
   * Remove all splits from a transaction
   */
  removeSplits: async (transactionId: string): Promise<void> => {
    return api.delete(`/transactions/${transactionId}/splits`);
  },

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get transactions for a specific account
   */
  getByAccount: async (
    accountId: string,
    params: Omit<TransactionsListParams, 'accountId'> = {}
  ): Promise<TransactionsListResponse> => {
    return transactionsService.getAll({ ...params, accountId });
  },

  /**
   * Get transactions for a specific category
   */
  getByCategory: async (
    categoryId: string,
    params: Omit<TransactionsListParams, 'categoryId'> = {}
  ): Promise<TransactionsListResponse> => {
    return transactionsService.getAll({ ...params, categoryId });
  },

  /**
   * Get uncategorized transactions
   */
  getUncategorized: async (
    params: Omit<TransactionsListParams, 'categoryId'> = {}
  ): Promise<TransactionsListResponse> => {
    return transactionsService.getAll({ ...params, categoryId: 'uncategorized' });
  },

  /**
   * Get pending transactions
   */
  getPending: async (
    params: Omit<TransactionsListParams, 'pending'> = {}
  ): Promise<TransactionsListResponse> => {
    return transactionsService.getAll({ ...params, pending: true });
  },

  /**
   * Get recent transactions (last 30 days)
   */
  getRecent: async (limit: number = 10): Promise<TransactionWithSplits[]> => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const response = await transactionsService.getAll({
      startDate: thirtyDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      pageSize: limit,
      sort: { field: 'date', direction: 'desc' },
    });

    return response.transactions;
  },

  /**
   * Update transaction notes
   */
  updateNotes: async (id: string, notes: string): Promise<Transaction> => {
    return transactionsService.update(id, { notes });
  },

  /**
   * Update transaction category
   */
  updateCategory: async (id: string, categoryId: string): Promise<Transaction> => {
    return transactionsService.update(id, { categoryId });
  },

  /**
   * Soft delete a transaction
   * Backend returns 204 No Content on success
   */
  delete: async (transactionId: string): Promise<void> => {
    await api.delete(`/transactions/${transactionId}`);
  },
};

// ============================================================================
// Transfer Detection Types (WP-TRANSFER-1)
// ============================================================================

export interface MarkTransferRequest {
  targetAccountId: string;
  pairedTransactionId?: string;
}

export interface MarkTransferResponse {
  transaction: TransactionWithSplits;
  similarCount: number;
}

export interface TransferCandidate {
  id: string;
  name: string;
  amount: number;
  date: string;
  matchScore: number;
}

export interface SimilarTransferSuggestion {
  transaction: TransactionWithSplits;
  suggestedPair?: TransactionWithSplits;
  confidence: number;
}

export interface SuggestSimilarTransfersResponse {
  count: number;
  suggestions: SimilarTransferSuggestion[];
}

export interface BatchMarkTransferItem {
  transactionId: string;
  targetAccountId: string;
  pairedTransactionId?: string;
}

export interface BatchMarkTransfersRequest {
  transfers: BatchMarkTransferItem[];
}

export interface BatchMarkTransfersResponse {
  successCount: number;
  failureCount: number;
  earliestDate: string;
  latestDate: string;
}

export interface SplitLoanPaymentRequest {
  interestAmount: number;
  loanAccountId: string;
}

export interface SplitLoanPaymentResponse {
  interestTransaction: TransactionWithSplits;
  principalTransaction: TransactionWithSplits;
}

// ============================================================================
// Transfer Reassignment Types (WP-TRANSFER-4)
// ============================================================================

export interface ReassignTransferRequest {
  targetAccountId: string;
  pairedTransactionId?: string;
}

export interface ReassignTransferResponse {
  transaction: TransactionWithSplits;
  previousPairId?: string;
  newPairId?: string;
}

// ============================================================================
// Transfer Detection Service Methods (WP-TRANSFER-1)
// ============================================================================

export const transferService = {
  /**
   * Mark a transaction as a transfer to another account
   */
  markAsTransfer: async (
    transactionId: string,
    request: MarkTransferRequest
  ): Promise<MarkTransferResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/transactions/${transactionId}/mark-transfer`,
      {
        targetAccountId: request.targetAccountId,
        pairedTransactionId: request.pairedTransactionId,
      }
    );

    return {
      transaction: transformTransaction(response.transaction),
      similarCount: response.similarCount ?? response.similar_count ?? 0,
    };
  },

  /**
   * Remove transfer marking from a transaction
   */
  unmarkTransfer: async (transactionId: string): Promise<TransactionWithSplits> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/transactions/${transactionId}/unmark-transfer`
    );
    return transformTransaction(response);
  },

  /**
   * Get potential transfer pair candidates from a target account
   */
  getTransferCandidates: async (
    transactionId: string,
    targetAccountId: string
  ): Promise<TransferCandidate[]> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any[]>(
      `/transactions/${transactionId}/transfer-candidates`,
      { targetAccountId }
    );

    return response.map((c) => ({
      id: c.id,
      name: c.name,
      amount: Number(c.amount),
      date: c.date,
      matchScore: c.matchScore ?? c.match_score ?? 0,
    }));
  },

  /**
   * Find similar transactions that might also be transfers
   */
  suggestSimilarTransfers: async (
    transactionId: string,
    limit: number = 20
  ): Promise<SuggestSimilarTransfersResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(
      `/transactions/${transactionId}/suggest-similar-transfers`,
      { limit }
    );

    return {
      count: response.count ?? 0,
      suggestions: (response.suggestions ?? []).map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (s: any): SimilarTransferSuggestion => ({
          transaction: transformTransaction(s.transaction),
          suggestedPair: s.suggestedPair
            ? transformTransaction(s.suggestedPair)
            : s.suggested_pair
            ? transformTransaction(s.suggested_pair)
            : undefined,
          confidence: s.confidence ?? 0,
        })
      ),
    };
  },

  /**
   * Batch mark multiple transactions as transfers
   */
  batchMarkTransfers: async (
    transfers: BatchMarkTransferItem[]
  ): Promise<BatchMarkTransfersResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>('/transactions/batch-mark-transfers', {
      transfers: transfers.map((t) => ({
        transactionId: t.transactionId,
        targetAccountId: t.targetAccountId,
        pairedTransactionId: t.pairedTransactionId,
      })),
    });

    return {
      successCount: response.successCount ?? response.success_count ?? 0,
      failureCount: response.failureCount ?? response.failure_count ?? 0,
      earliestDate: response.earliestDate ?? response.earliest_date ?? '',
      latestDate: response.latestDate ?? response.latest_date ?? '',
    };
  },

  /**
   * Split a loan payment into interest and principal transactions
   */
  splitLoanPayment: async (
    transactionId: string,
    request: SplitLoanPaymentRequest
  ): Promise<SplitLoanPaymentResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/transactions/${transactionId}/split-loan-payment`,
      {
        interestAmount: request.interestAmount,
        loanAccountId: request.loanAccountId,
      }
    );

    return {
      interestTransaction: transformTransaction(
        response.interestTransaction ?? response.interest_transaction
      ),
      principalTransaction: transformTransaction(
        response.principalTransaction ?? response.principal_transaction
      ),
    };
  },

  /**
   * WP-TRANSFER-4: Reassign a transfer to a different account/transaction.
   * Changes the target account and optionally pairs with a specific transaction.
   */
  reassignTransfer: async (
    transactionId: string,
    request: ReassignTransferRequest
  ): Promise<ReassignTransferResponse> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/transactions/${transactionId}/reassign-transfer`,
      {
        targetAccountId: request.targetAccountId,
        pairedTransactionId: request.pairedTransactionId,
      }
    );

    return {
      transaction: transformTransaction(response.transaction),
      previousPairId: response.previousPairId ?? response.previous_pair_id,
      newPairId: response.newPairId ?? response.new_pair_id,
    };
  },

  /**
   * WP-TRANSFER-4: Unlink a transfer pair without unmarking the transfer.
   * Removes the transfer_pair_id but keeps is_transfer=true.
   */
  unlinkTransfer: async (transactionId: string): Promise<TransactionWithSplits> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(
      `/transactions/${transactionId}/unlink-transfer`
    );
    return transformTransaction(response.transaction);
  },
};

/**
 * Transform raw API transaction response to frontend TransactionWithSplits type.
 * Single source of truth for all transaction transformations.
 *
 * Handles:
 * - Dual-casing (camelCase/snake_case) for API compatibility
 * - Decimal string to number conversion for amount fields
 * - Nested transferPairTransaction object transformation
 * - WP-TRANSFER-4 transfer context fields
 *
 * @param txn - Raw transaction object from API response
 * @returns Properly typed TransactionWithSplits
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transformTransaction(txn: any): TransactionWithSplits {
  const amount = Number(txn.amount) || 0;
  const isTransfer = txn.isTransfer ?? txn.is_transfer ?? false;

  // WP-API-9: Use backend-provided type directly
  // Falls back to computed value for backwards compatibility with cached responses
  const type: 'income' | 'expense' | 'transfer' =
    (txn.type as 'income' | 'expense' | 'transfer') ??
    (isTransfer ? 'transfer' : amount > 0 ? 'income' : 'expense');

  // WP-TRANSFER-4: Transform transfer pair transaction summary
  const rawPairTxn = txn.transferPairTransaction ?? txn.transfer_pair_transaction;
  let transferPairTransaction: TransferPairTransaction | undefined;
  if (rawPairTxn) {
    transferPairTransaction = {
      id: rawPairTxn.id,
      name: rawPairTxn.name ?? 'Unknown Transaction',
      amount: Number(rawPairTxn.amount) || 0,
      date: rawPairTxn.date ?? '',
      accountId: rawPairTxn.accountId ?? rawPairTxn.account_id ?? '',
      accountName: rawPairTxn.accountName ?? rawPairTxn.account_name ?? '',
    };
  }

  const result: TransactionWithSplits = {
    id: txn.id,
    tenantId: txn.tenantId ?? txn.tenant_id ?? '',
    accountId: txn.accountId ?? txn.account_id ?? '',
    amount,
    currency: txn.currency ?? 'USD',
    date: txn.date ?? txn.transactionDate ?? txn.transaction_date ?? '',
    name: txn.name ?? 'Unknown Transaction',
    merchantName: txn.merchantName ?? txn.merchant_name,
    categoryId: txn.categoryId ?? txn.category_id,
    categoryName: txn.category?.name ?? txn.categoryName,
    categoryPath: txn.category?.path ?? txn.categoryPath,
    pending: txn.pending ?? false,
    type,
    createdAt: txn.createdAt ?? txn.created_at ?? '',
    updatedAt: txn.updatedAt ?? txn.updated_at ?? '',
    // TransactionWithSplits extensions
    isTransfer,
    isSplit: txn.isSplit ?? txn.is_split ?? false,
    // WP-TRANSFER-4: Transfer pair fields
    transferPairId: txn.transferPairId ?? txn.transfer_pair_id,
    transferTargetAccountId: txn.transferTargetAccountId ?? txn.transfer_target_account_id,
    transferTargetAccountName: txn.transferTargetAccountName ?? txn.transfer_target_account_name,
    transferPairTransaction,
  };

  return result;
}

export default transactionsService;
